import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import RouteFallback from "@/components/RouteFallback";
import { usePageTracking } from "@/hooks/useAnalytics";
import CookieConsent from "@/components/CookieConsent";
import { initLinkedInInsight } from "@/lib/linkedin";
import Index from "./pages/Index";

const Admin = lazy(() => import("./pages/Admin"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const InnovationBlueprint = lazy(() => import("./pages/InnovationBlueprint"));
const ResilienceScore = lazy(() => import("./pages/ResilienceScore"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Organizations = lazy(() => import("./pages/Organizations"));
const Approach = lazy(() => import("./pages/Approach"));
const About = lazy(() => import("./pages/About"));
const Work = lazy(() => import("./pages/Work"));
const ConsumerHardware = lazy(() => import("./pages/work/ConsumerHardware"));
const Sukham = lazy(() => import("./pages/work/Sukham"));
const Product = lazy(() => import("./pages/services/Product"));
const GoToMarket = lazy(() => import("./pages/services/Systems"));
const Operations = lazy(() => import("./pages/services/Operations"));
const Capabilities = lazy(() => import("./pages/Capabilities"));
const Careers = lazy(() => import("./pages/Careers"));

const queryClient = new QueryClient();

const PageTracker = () => {
  usePageTracking();
  return null;
};

const App = () => {
  useEffect(() => initLinkedInInsight(), []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/organizations" element={<Organizations />} />
            <Route path="/approach" element={<Approach />} />
            <Route path="/about" element={<About />} />
            <Route path="/capabilities" element={<Capabilities />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/services/product" element={<Product />} />
            <Route path="/services/go-to-market" element={<GoToMarket />} />
            <Route path="/services/systems" element={<Navigate to="/services/go-to-market" replace />} />
            <Route path="/services/operations" element={<Operations />} />
            <Route path="/work" element={<Work />} />
            <Route path="/work/consumer-hardware" element={<ConsumerHardware />} />
            <Route path="/work/sukham" element={<Sukham />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/resources/innovation-blueprint" element={<InnovationBlueprint />} />
            <Route path="/resources/resilience-score" element={<ResilienceScore />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <CookieConsent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
